import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { creditMeter, creditsLabel, mediaUsageSummary, resetDay } from '@church/domain'
import {
  ACCESS_APP_LABELS,
  ACCESS_MODULE_LABELS,
  ACCESS_MODULES,
  highest,
  isLevelOf,
  LEVEL_LABELS,
  ROLE_PERMISSION_LABELS,
} from '@church/permissions'
import { useApi } from '../api-context.tsx'
import { memberKeys } from '../members/keys.ts'
import { sessionKeys } from '../session/keys.ts'
import { platformKeys } from './keys.ts'

/**
 * Settings for administrators: roles, billing, API keys and webhooks, the
 * full export and account deletion, and the audit log.
 */

// ---- roles -----------------------------------------------------------------------------

/**
 * The roles (Settings → Roles), read only: the presets, with the levels each
 * gives and what stays on the role, and the custom roles a church made
 * before templates, which can only be deleted. Giving someone a role is
 * `assign` (staff); custom roles are no longer made or changed.
 */
export function useAccessRoles() {
  const api = useApi()
  const queryClient = useQueryClient()
  const roles = useQuery({
    queryKey: platformKeys.roles(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.access.roles.get()
      if (error) throw error
      return data
    },
  })
  const put = (data: unknown) => queryClient.setQueryData(platformKeys.roles(), data)
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.access.roles({ id }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const assign = useMutation({
    mutationFn: async ({ memberId, roles: list }: { memberId: string; roles: string[] }) => {
      const { data, error } = await api.api.v1.members({ id: memberId }).roles.put({ roles: list })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.roles() })
      void queryClient.invalidateQueries({ queryKey: memberKeys.all() })
    },
  })
  const list = roles.data ?? []
  return {
    roles,
    remove,
    assign,
    /**
     * The roles a member's picker offers: every preset, and a custom role
     * only to someone who already holds it (they keep it; nobody gets it anew).
     */
    choicesFor: (held: readonly string[]) =>
      list.filter((role) => role.kind === 'preset' || held.includes(role.id)),
  }
}

/** What a role gives, as the Roles panel reads it. */
export interface RoleSummary {
  /** Its levels, grouped by app (ForServices, then ForChurch); empty for a custom role. */
  apps: { app: string; levels: { module: string; level: string }[] }[]
  /** What stays on the role: giving and the field permissions, in words. */
  also: string[]
}

export function roleSummary(role: {
  levels: readonly { module: string; level: string }[]
  permissions: readonly string[]
}): RoleSummary {
  const apps = (['services', 'church'] as const).map((app) => ({
    app: ACCESS_APP_LABELS[app],
    levels: ACCESS_MODULES[app].flatMap((module) => {
      const held = role.levels.filter((row) => row.module === module)
      const level = highest(
        held.flatMap((row) => (isLevelOf(module, row.level) ? [row.level] : [])),
      )
      return level ? [{ module: ACCESS_MODULE_LABELS[module], level: LEVEL_LABELS[level] }] : []
    }),
  }))
  return {
    apps: apps.filter((group) => group.levels.length > 0),
    also: role.permissions.map((permission) => ROLE_PERMISSION_LABELS[permission] ?? permission),
  }
}

// ---- billing -----------------------------------------------------------------------------

export function useBilling() {
  const api = useApi()
  const queryClient = useQueryClient()
  const overview = useQuery({
    queryKey: platformKeys.billing(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.billing.get()
      if (error) throw error
      return data
    },
  })
  const checkout = useMutation({
    mutationFn: async (plan: string) => {
      const { data, error } = await api.api.v1.billing.checkout.post({ plan })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.billing() })
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all() })
    },
  })
  const portal = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.billing.portal.post()
      if (error) throw error
      return data
    },
  })
  const simulate = useMutation({
    mutationFn: async (kind: 'payment_failed' | 'payment_succeeded' | 'canceled') => {
      const { data, error } = await api.api.v1.billing.test.simulate.post({ kind })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(platformKeys.billing(), data)
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all() })
    },
  })
  return { overview, checkout, portal, simulate }
}

/** The plan's media allowance: used, of what, and on what (photos, files, the library). */
export function useMediaUsage() {
  const api = useApi()
  const usage = useQuery({
    queryKey: platformKeys.mediaUsage(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.media.usage.get()
      if (error) throw error
      return data
    },
  })
  return { usage, summary: usage.data ? mediaUsageSummary(usage.data) : null }
}

/**
 * Settings › Billing's AI credits card (docs/15, "AI credits"): this month's
 * credits with a bar, the grants in them, what uses credits and what used
 * them. Hidden where AI is not set up.
 */
export function useAiCredits() {
  const api = useApi()
  const query = useQuery({
    queryKey: platformKeys.aiCredits(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.billing['ai-credits'].get()
      if (error) throw error
      return data
    },
  })
  const data = query.data
  const balance = data?.enabled ? data.balance : null
  return {
    query,
    shown: data?.enabled === true,
    balance,
    /** How much is left, 0–100 (null: unlimited). */
    meter: balance ? creditMeter(balance) : null,
    /** "Resets Nov 1". */
    resets: balance ? `Resets ${resetDay(balance.resetsOn)}` : null,
    grants: data?.grants ?? [],
    actions: (data?.actions ?? []).map((a) => ({ ...a, cost: creditsLabel(a.credits) })),
    use: (data?.use ?? []).map((u) => ({ ...u, total: creditsLabel(u.credits) })),
  }
}

/** A plan's monthly AI credits, as its card on the Billing page says it. */
export const planCreditsLine = (limits: Record<string, number | null | undefined>) => {
  const credits = limits.ai_credits
  return typeof credits === 'number'
    ? `${credits.toLocaleString()} AI credits a month`
    : 'Unlimited AI credits'
}

// ---- developers ----------------------------------------------------------------------------

export function useApiKeys() {
  const api = useApi()
  const queryClient = useQueryClient()
  const keys = useQuery({
    queryKey: platformKeys.apiKeys(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.developers['api-keys'].get()
      if (error) throw error
      return data
    },
  })
  const create = useMutation({
    mutationFn: async (body: { name: string; scopes: string[] }) => {
      const { data, error } = await api.api.v1.developers['api-keys'].post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: platformKeys.apiKeys() }),
  })
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.developers['api-keys']({ id }).delete()
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(platformKeys.apiKeys(), data),
  })
  return { keys, create, revoke }
}

export function useWebhooks() {
  const api = useApi()
  const queryClient = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)
  const endpoints = useQuery({
    queryKey: platformKeys.webhooks(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.developers.webhooks.get()
      if (error) throw error
      return data
    },
  })
  const events = useQuery({
    queryKey: platformKeys.events(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.developers.events.get()
      if (error) throw error
      return data
    },
    staleTime: Infinity,
  })
  const deliveries = useQuery({
    queryKey: platformKeys.deliveries(openId ?? ''),
    enabled: Boolean(openId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.developers
        .webhooks({ id: openId ?? '' })
        .deliveries.get()
      if (error) throw error
      return data
    },
    refetchInterval: 15_000,
  })
  const put = (data: unknown) => queryClient.setQueryData(platformKeys.webhooks(), data)
  const create = useMutation({
    mutationFn: async (body: { url: string; description?: string | null; events: string[] }) => {
      const { data, error } = await api.api.v1.developers.webhooks.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const update = useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      isActive?: boolean
      rotateSecret?: boolean
      events?: string[]
    }) => {
      const { data, error } = await api.api.v1.developers.webhooks({ id }).put(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.developers.webhooks({ id }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const putDeliveries = (data: unknown) =>
    queryClient.setQueryData(platformKeys.deliveries(openId ?? ''), data)
  const ping = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.developers.webhooks({ id }).ping.post()
      if (error) throw error
      return data
    },
    onSuccess: putDeliveries,
  })
  const retry = useMutation({
    mutationFn: async (deliveryId: string) => {
      const { data, error } = await api.api.v1.developers
        .deliveries({ id: deliveryId })
        .retry.post()
      if (error) throw error
      return data
    },
    onSuccess: putDeliveries,
  })
  return {
    endpoints,
    events,
    openId,
    open: setOpenId,
    deliveries,
    create,
    update,
    remove,
    ping,
    retry,
  }
}

// ---- account -------------------------------------------------------------------------------

export function useAccount() {
  const api = useApi()
  const queryClient = useQueryClient()
  const exports = useQuery({
    queryKey: platformKeys.exports(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.account.exports.get()
      if (error) throw error
      return data
    },
    refetchInterval: (query) =>
      query.state.data?.some((e) => e.status === 'queued' || e.status === 'running')
        ? 2_000
        : false,
  })
  const requestExport = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.account.exports.post()
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(platformKeys.exports(), data),
  })
  const deletion = useQuery({
    queryKey: platformKeys.deletion(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.account.deletion.get()
      if (error) throw error
      return data
    },
  })
  const requestDeletion = useMutation({
    mutationFn: async (confirmName: string) => {
      const { data, error } = await api.api.v1.account.deletion.post({ confirmName })
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(platformKeys.deletion(), data),
  })
  const cancelDeletion = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.account.deletion.delete()
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(platformKeys.deletion(), data),
  })
  return { exports, requestExport, deletion, requestDeletion, cancelDeletion }
}

export function useAuditLog(filters: { action?: string; entityType?: string }) {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: platformKeys.audit(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1['audit-log'].get({
        query: {
          limit: 50,
          ...(filters.action ? { action: filters.action } : {}),
          ...(filters.entityType ? { entityType: filters.entityType } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
  return {
    entries: query.data?.pages.flatMap((p) => p.items) ?? [],
    isLoading: query.isPending,
    error: query.error,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  }
}
